import React, { useContext } from 'react';
import { LinkProps as RouterLinkProps } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { batch } from '../App';
import { useSelector, useDispatch } from '../actions/types';
import { Actions as SearchActions } from '../actions/search';
import { SearchResultContext, ISearchResultWithIndex } from '../contexts';
import useSearchResults from '../hooks/useSearchResults';
import { Context as ConfigContext } from '../Config';
import * as Meta from './meta';
import * as Article from './article';
import * as Category from './category';

/**
 * Print out the total number of search results available.
 * NOTE: this will print <null> if we don't have search results yet.
 */
export const TotalCount: React.FC = () => {
  const { totalCount } = useSearchResults();
  return <>{totalCount}</>;
};

/**
 * Print out the search term that the current results are showing for.
 * NOTE: will print out <null> if there isn't a current search.
 */
export const SearchTerm: React.FC = () => {
  const { searchTerm } = useSearchResults();
  return <>{searchTerm}</>;
};

/**
 * Print out the article title from the current search result.
 */
export const ArticleTitle = () => {
  const searchResult = React.useContext(SearchResultContext);
  if (!searchResult) return null;
  return <>{searchResult.title}</>;
};

type ArticleLinkProps = Omit<RouterLinkProps, 'to'> & {
  articleId?: string; // TODO: allow to specify article id.
};
/**
 * This is an A tag with a link to the search results article
 */
export const ArticleLink: React.FC<ArticleLinkProps> = props => {
  const config = useContext(ConfigContext);
  const dispatch = useDispatch();
  const searchResult = React.useContext(SearchResultContext);

  const onClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    searchResult &&
      dispatch(
        SearchActions.searchResultClick(
          searchResult.id,
          searchResult.title,
          searchResult.index
        )
      );

    // Doing this to make sure the event has a chance to be sent.
    if (config.forceRefreshRouting) {
      e.preventDefault();
      batch.flush(true);
      window.location.href = e.currentTarget.href;
    }
  };

  if (!searchResult) return null;
  return <Article.Link {...props} slug={searchResult.slug} onClick={onClick} />;
};

/**
 * Print out the article intro from the current search result.
 */
// export const Intro = () => {
//   const searchResult = React.useContext(SearchResultContext);
//   if (!searchResult) return null;
//   return <>{truncate(searchResult.bodyText, {'length': 200})}</>;
// };

/**
 * Print out the article last updated time from the current search result.
 */
// export const ArticleUpdatedAt = () => {
//   const searchResult = React.useContext(SearchResultContext);
//   if (!searchResult) return null;
//   return <>{format(new Date(searchResult.lastUpdatedAt), 'MMM d, yyyy h:mma')}</>;
// };

/**
 * Print out the title of the category of the current search result.
 */
export const CategoryTitle = () => {
  const searchResult = React.useContext(SearchResultContext);
  if (!searchResult) return null;
  return <>{searchResult.category.title}</>;
};

type CategoryLinkProps = Omit<RouterLinkProps, 'to'>;
/**
 * Link to the category of the current search result.
 */
export const CategoryLink: React.FC<CategoryLinkProps> = props => {
  const searchResult = React.useContext(SearchResultContext);
  if (!searchResult) return null;
  return <Category.Link {...props} categoryId={searchResult.category.id} />;
};

type DivProps = JSX.IntrinsicElements['div'];

/**
 * This renders all the body snippets that match the current search for the current result.
 * @param props any props that you can pass to a div
 */
export const Snippet: React.FC<DivProps> = props => {
  const searchResult = React.useContext(SearchResultContext);
  if (
    !(
      searchResult &&
      searchResult.highlights &&
      searchResult.highlights.body.length > 0
    )
  )
    return null;
  return <Meta.HTML {...props}>{searchResult.highlights.body[0]}</Meta.HTML>;
};

export type SearchResultFunction = (data: {
  result: ISearchResultWithIndex;
}) => React.ReactNode;

export const isSearchResultFunction = (
  arg: any
): arg is SearchResultFunction => {
  return typeof arg === 'function';
};

export function returnChild(
  children: SearchResultFunction | React.ReactNode,
  result: ISearchResultWithIndex
) {
  return React.isValidElement(children)
    ? children
    : isSearchResultFunction(children)
    ? children({ result })
    : null;
}

type LoopResultsProps = {
  children: React.ReactNode | SearchResultFunction;
};
/**
 * For each of the search results found it repeats the children passed in.
 * So if you pass a render prop as a child it will be called with each search result.
 * Else if you pass in React Elements they will be repeated for each search result.
 */
export const LoopResults: React.FC<LoopResultsProps> = ({ children }) => {
  const { results } = useSearchResults();
  if (!results) return null;

  return (
    <>
      {results.map((result, index) => {
        const withIndex = { ...result, index };
        return (
          <SearchResultContext.Provider key={result.id} value={withIndex}>
            {returnChild(children, withIndex)}
          </SearchResultContext.Provider>
        );
      })}
    </>
  );
};

type IsEmptyProps = {};
/**
 * Render the content if the search results ARE empty
 */
export const IsEmpty: React.FC<IsEmptyProps> = ({ children }) => {
  const count = useSelector(state => state.search.totalCount);
  const searchedTerm = useSelector(state => state.search.searchedTerm);
  if (!searchedTerm) return null;
  if (count != null && count === 0) return <>{children}</>;
  return null;
};

type IsNotEmptyProps = {};
/**
 * Render the content if the search results ARE NOT empty
 */
export const IsNotEmpty: React.FC<IsNotEmptyProps> = ({ children }) => {
  const count = useSelector(state => state.search.totalCount);
  if (count != null && count > 0) return <>{children}</>;
  return null;
};

export const ShowMoreButton: React.FC = ({ ...props }) => {
  const { isFetching, loadMore, hasMore } = useSearchResults();
  if (!hasMore) return null;

  return (
    <button disabled={isFetching || !hasMore} onClick={loadMore} {...props}>
      <Trans i18nKey="search.loadMore">Load more</Trans>
      {isFetching && '---Loading'}
    </button>
  );
};
